import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import ScrollToTop from "./ScrollToTop";
import SEO from "../components/Layout/SEO";

const PrivacyPolicyPage = () => {
  const theme = useTheme();

  const sections = [
    {
      title: "1. Introduction",
      content: `ProtoDiag Solutions Pvt. Ltd. ("we," "our," or "us") is a telecom technology company based in Noida, India, specializing in high-performance messaging platforms, voice communications, and telecom infrastructure solutions. We are committed to protecting your privacy and the confidentiality of all communication data processed through our services. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our telecom services (including our Bulk Messaging Gateway, Voice Communications Platform, SMSC, SMS Firewall, and other products), or interact with our APIs. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access our website or use our services.`,
    },
    {
      title: "2. Information We Collect",
      content: `As a telecom service provider, we collect various types of information necessary to deliver our communication services and operate our platform. The information we collect includes:`,
      subsections: [
        {
          title: "2.1 Business and Contact Information",
          content: `When you register for our services, contact us through our website, or request information about our products, we collect business information such as your name, email address, phone number, company name, organization details, billing address, and any other information you voluntarily provide. This information is essential for account management, service delivery, and customer support.`,
        },
        {
          title: "2.2 Communication Data (SMS and Voice)",
          content: `In the course of providing our telecom services, we process communication data including: SMS content and metadata (sender/receiver phone numbers, message text, timestamps, delivery status), voice call records (CDR - Call Detail Records), IVR interaction data, delivery receipts (DLR), and routing information. This data is processed to deliver messages and voice calls, generate delivery reports, ensure compliance with telecom regulations, and provide analytics to our enterprise clients.`,
        },
        {
          title: "2.3 API and Platform Usage Data",
          content: `When you use our Telecom API Gateway, Bulk Messaging Gateway, or other platform services, we collect API usage data including authentication credentials, request/response logs, transaction volumes, routing decisions, performance metrics, and billing information. This data helps us optimize service delivery, troubleshoot issues, and generate accurate billing records.`,
        },
        {
          title: "2.4 Website and Technical Data",
          content: `When you visit our website, we automatically collect certain technical information about your device, including your IP address, web browser type, time zone, referring website, pages viewed, and interaction patterns. We use cookies and similar tracking technologies to enhance your browsing experience and analyze website traffic.`,
        },
        {
          title: "2.5 Compliance and Regulatory Data",
          content: `To comply with telecom regulations and DLT (Distributed Ledger Technology) requirements in India, we may collect and maintain data related to message templates, sender registrations, consent records, and regulatory compliance documentation as required by law.`,
        },
      ],
    },
    {
      title: "3. How We Use Your Information",
      content: `We use the information we collect for the following purposes:`,
      list: [
        "Service Delivery: To provide, operate, and maintain our telecom services including SMS delivery, voice communications, SMSC operations, and API gateway services",
        "Message Routing and Delivery: To route SMS messages and voice calls through our network, optimize delivery paths, generate delivery receipts (DLR), and ensure successful message/call completion",
        "Billing and Reconciliation: To generate accurate billing records, process payments, reconcile CDR/DLR data, and manage enterprise accounts",
        "Compliance and Security: To comply with telecom regulations, DLT requirements, detect and prevent spam/fraud (through our SMS Firewall and SPAM Guard services), and ensure regulatory compliance",
        "Analytics and Reporting: To provide real-time analytics, delivery reports, call detail records, and performance insights to our enterprise clients",
        "Customer Support: To respond to your inquiries, provide technical support, troubleshoot service issues, and communicate service updates",
        "Service Improvement: To analyze usage patterns, optimize platform performance, develop new features, and enhance our telecom infrastructure",
        "Marketing and Communication: To send you product updates, service announcements, and marketing communications (with your consent) about our telecom solutions",
        "Legal Compliance: To comply with legal obligations, respond to lawful requests from authorities, and protect our rights and the rights of our users",
      ],
    },
    {
      title: "4. Disclosure of Your Information",
      content: `As a telecom service provider, we may share information in the following circumstances:`,
      subsections: [
        {
          title: "4.1 Mobile Network Operators (MNOs) and Carriers",
          content: `To deliver SMS messages and voice calls, we share necessary routing and delivery data with mobile network operators, telecom carriers, and interconnect partners. This includes phone numbers, message routing information, and delivery status data required for message/call completion.`,
        },
        {
          title: "4.2 Enterprise Clients",
          content: `When you use our services as an enterprise client, we share delivery reports (DLR), call detail records (CDR), analytics data, and performance metrics with your organization through our platform dashboards and APIs, as per your service agreement.`,
        },
        {
          title: "4.3 Regulatory and Legal Compliance",
          content: `We may disclose information to comply with telecom regulations, DLT requirements, respond to lawful requests from government authorities, regulatory bodies (such as TRAI in India), or to investigate fraud, spam, or security threats. This includes sharing data required for regulatory reporting and compliance audits.`,
        },
        {
          title: "4.4 Third-Party Service Providers",
          content: `We engage trusted third-party service providers who assist us in operating our platform, including cloud hosting providers, payment processors, analytics services, and technical support vendors. These providers are contractually bound to protect your information and use it only for specified purposes.`,
        },
        {
          title: "4.5 Business Transfers",
          content: `In the event of a merger, acquisition, sale of assets, or business transfer, your information may be transferred to the acquiring entity, subject to the same privacy protections outlined in this policy.`,
        },
        {
          title: "4.6 With Your Consent",
          content: `We may share your information with other parties when you explicitly consent to such sharing, such as when you request integration with third-party services or authorize specific data sharing arrangements.`,
        },
      ],
    },
    {
      title: "5. Data Security and Protection",
      content: `As a carrier-grade telecom platform handling high-volume communications, we implement robust security measures to protect your data:`,
      subsections: [
        {
          title: "5.1 Technical Safeguards",
          content: `We employ industry-standard encryption (TLS/SSL) for data in transit, secure API authentication mechanisms, encrypted message storage, and protected database systems. Our SMS Firewall and security systems actively monitor and prevent unauthorized access, spam, and fraud.`,
        },
        {
          title: "5.2 Infrastructure Security",
          content: `Our platform operates on secure, high-availability infrastructure with redundant systems, regular security audits, access controls, and monitoring. We maintain strict access controls to ensure only authorized personnel can access sensitive communication data.`,
        },
        {
          title: "5.3 Data Retention and Deletion",
          content: `We retain communication data (SMS, CDR, DLR) only as long as necessary for service delivery, billing, compliance, and legal requirements. Message content is typically retained for a limited period as per telecom regulations and our service agreements. You may request deletion of your account data, subject to legal and regulatory retention requirements.`,
        },
        {
          title: "5.4 Limitations",
          content: `While we implement comprehensive security measures, no system is 100% secure. We cannot guarantee absolute security against all threats, but we continuously work to enhance our security posture and respond promptly to any security incidents.`,
        },
      ],
    },
    {
      title: "6. Your Privacy Rights and Choices",
      content: `As a user of our services, you have the following rights regarding your information:`,
      list: [
        "Access Rights: You can request access to your account information, billing records, and communication data (subject to legal and regulatory restrictions)",
        "Correction Rights: You can update and correct your account information, contact details, and business information through your account dashboard or by contacting us",
        "Data Portability: Enterprise clients can export their delivery reports (DLR), call detail records (CDR), and analytics data through our APIs or platform dashboards",
        "Opt-Out of Marketing: You can opt-out of marketing communications by clicking unsubscribe links in our emails or contacting us directly",
        "Account Deletion: You can request deletion of your account and associated data, subject to our legal obligations to retain certain data for regulatory compliance, billing, and fraud prevention",
        "API Access Control: Enterprise clients can manage API credentials, access permissions, and control data sharing through our platform's access management features",
        "Cookie Preferences: You can manage cookie preferences through your browser settings, though some cookies are essential for our platform to function properly",
      ],
      note: "Please note that certain communication data (SMS content, CDR records) may be subject to retention requirements under telecom regulations and cannot be immediately deleted upon request.",
    },
    {
      title: "7. Data Retention and Storage",
      content: `We retain different types of data for varying periods based on business needs and legal requirements:`,
      subsections: [
        {
          title: "7.1 Communication Data",
          content: `SMS content, delivery receipts (DLR), and call detail records (CDR) are typically retained for a period necessary to provide service delivery, generate billing records, and comply with telecom regulations. Message content may be retained for shorter periods, while metadata and delivery records may be retained longer for billing and compliance purposes.`,
        },
        {
          title: "7.2 Account and Business Data",
          content: `Your account information, business details, and contact information are retained for as long as your account is active and for a reasonable period thereafter to comply with legal obligations and resolve disputes.`,
        },
        {
          title: "7.3 Regulatory Compliance",
          content: `Certain data must be retained for extended periods as required by telecom regulations, DLT requirements, tax laws, and other applicable regulations in India and other jurisdictions where we operate.`,
        },
        {
          title: "7.4 Data Deletion",
          content: `Upon account termination or deletion request, we will delete or anonymize your data within a reasonable timeframe, except where retention is required by law or for legitimate business purposes such as fraud prevention.`,
        },
      ],
    },
    {
      title: "8. Telecom Regulations and Compliance",
      content: `As a telecom service provider, we operate in compliance with applicable telecom regulations:`,
      subsections: [
        {
          title: "8.1 India DLT Compliance",
          content: `For services in India, we comply with the Distributed Ledger Technology (DLT) requirements for commercial messaging. All promotional messages are sent through registered templates and sender IDs as per TRAI guidelines.`,
        },
        {
          title: "8.2 Spam Prevention",
          content: `Our SMS Firewall and SPAM Guard services actively detect and prevent unsolicited commercial communication (UCC) in compliance with telecom regulations. We implement content filtering, traffic pattern analysis, and regulatory reporting as required.`,
        },
        {
          title: "8.3 Regulatory Reporting",
          content: `We may be required to share certain data with regulatory authorities, including TRAI in India, for compliance audits, spam investigations, and regulatory reporting as mandated by law.`,
        },
        {
          title: "8.4 International Compliance",
          content: `For international messaging and voice services, we comply with applicable regulations in the countries where we operate, including data protection laws, telecom regulations, and cross-border data transfer requirements.`,
        },
      ],
    },
    {
      title: "9. Third-Party Services and Integrations",
      content: `Our platform integrates with various third-party services and telecom operators:`,
      subsections: [
        {
          title: "9.1 Mobile Network Operators",
          content: `To deliver SMS and voice services, we integrate with mobile network operators (MNOs) and carriers. These operators have their own privacy policies governing how they handle communication data.`,
        },
        {
          title: "9.2 Enterprise Integrations",
          content: `Our Telecom API Gateway allows enterprise clients to integrate our services with their CRM systems, business applications, and other third-party platforms. Data shared through these integrations is subject to the privacy policies of those third-party services.`,
        },
        {
          title: "9.3 Analytics and Reporting Tools",
          content: `We may use third-party analytics tools and reporting platforms (such as Power BI, Grafana, ELK stack) to provide insights and analytics to our clients. These tools process aggregated and anonymized data.`,
        },
      ],
    },
    {
      title: "10. Changes to This Privacy Policy",
      content: `We may update this Privacy Policy from time to time to reflect changes in our services, legal requirements, or business practices. We will notify you of material changes by:`,
      list: [
        "Posting the updated Privacy Policy on this page with a revised 'Last Updated' date",
        "Sending email notifications to registered account holders for significant changes",
        "Displaying prominent notices on our platform dashboard for enterprise clients",
      ],
      note: "Your continued use of our services after any changes indicates your acceptance of the updated Privacy Policy. We encourage you to review this policy periodically.",
    },
    {
      title: "11. Contact Us",
      content: `If you have questions, concerns, or requests regarding this Privacy Policy, your data, or our privacy practices, please contact us:`,
      subsections: [
        {
          title: "11.1 General Privacy Inquiries",
          content: `For general privacy questions, data access requests, or privacy concerns, please contact our Privacy Team.`,
        },
        {
          title: "11.2 Enterprise Client Support",
          content: `Enterprise clients with service agreements can contact their dedicated account manager or our technical support team for data-related requests and platform access management.`,
        },
        {
          title: "11.3 Regulatory and Compliance",
          content: `For regulatory compliance inquiries or data requests from authorized authorities, please contact our compliance team with proper documentation and authorization.`,
        },
      ],
      contactInfo: {
        email: "info@protodiag.com",
        phone: "+91-9899817807 / +91-9910274500",
        address:
          "1st floor, A-5, Grovy Optiva, Block A, Sector 68, Noida, Basi Bahuddin Nagar, Uttar Pradesh 201316",
      },
      note: "We aim to respond to all privacy-related inquiries within 30 days, subject to the complexity of the request and applicable legal requirements.",
    },
  ];

  return (
    <Box>
      <SEO
        title="Privacy Policy"
        description="Privacy Policy of ProtoDiag Solutions - Learn how we collect, use, and protect your personal information and communication data in compliance with telecom regulations."
        keywords="ProtoDiag privacy policy, data protection, telecom privacy, communication data privacy, GDPR compliance"
        url="https://www.protodiag.com/privacy-policy"
      />
      <ScrollToTop />

      {/* Hero Section */}
      <Box
        sx={{
          py: 10,
          backgroundColor: "primary.main",
          color: "white",
        }}
      >
        <Container>
          <Typography
            variant="h2"
            component="h1"
            fontWeight="bold"
            gutterBottom
          >
            Privacy Policy
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 800, opacity: 0.9 }}>
            Your privacy is important to us. This policy explains how we
            collect, use, and protect your personal information.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, opacity: 0.8 }}>
            Last Updated:{" "}
            {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </Typography>
        </Container>
      </Box>

      {/* Content Section */}
      <Box sx={{ py: 8, backgroundColor: "background.default" }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 6 },
              backgroundColor: "background.paper",
              borderRadius: 2,
            }}
          >
            {sections.map((section, index) => (
              <Box key={index} sx={{ mb: 5 }}>
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: "primary.main",
                  }}
                >
                  {section.title}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: section.subsections || section.list ? 3 : 0,
                    lineHeight: 1.8,
                    color: "text.primary",
                  }}
                >
                  {section.content}
                </Typography>

                {section.list && (
                  <Box
                    component="ul"
                    sx={{
                      pl: 3,
                      mb: section.note ? 2 : 3,
                      "& li": {
                        mb: 1.5,
                        lineHeight: 1.8,
                        color: "text.primary",
                      },
                    }}
                  >
                    {section.list.map((item, itemIndex) => (
                      <li key={itemIndex}>{item}</li>
                    ))}
                  </Box>
                )}

                {section.note && (
                  <Box
                    sx={{
                      mt: 2,
                      mb: 3,
                      p: 2,
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      borderLeft: `4px solid ${theme.palette.warning.main}`,
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "italic",
                        color: "text.secondary",
                        lineHeight: 1.7,
                      }}
                    >
                      <strong>Note:</strong> {section.note}
                    </Typography>
                  </Box>
                )}

                {section.subsections &&
                  section.subsections.map((subsection, subIndex) => (
                    <Box
                      key={subIndex}
                      sx={{
                        mt: 3,
                        pl: { xs: 2, md: 3 },
                        borderLeft: `3px solid ${alpha(
                          theme.palette.primary.main,
                          0.3,
                        )}`,
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 1.5,
                          color: "text.primary",
                        }}
                      >
                        {subsection.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          lineHeight: 1.8,
                          color: "text.secondary",
                        }}
                      >
                        {subsection.content}
                      </Typography>
                    </Box>
                  ))}

                {section.contactInfo && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 3,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: 1.5 }}>
                      <strong>Email:</strong> {section.contactInfo.email}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1.5 }}>
                      <strong>Phone:</strong> {section.contactInfo.phone}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Address:</strong> {section.contactInfo.address}
                    </Typography>
                  </Box>
                )}

                {section.note && section.contactInfo && (
                  <Box
                    sx={{
                      mt: 2,
                      mb: 3,
                      p: 2,
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      borderLeft: `4px solid ${theme.palette.warning.main}`,
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "italic",
                        color: "text.secondary",
                        lineHeight: 1.7,
                      }}
                    >
                      <strong>Note:</strong> {section.note}
                    </Typography>
                  </Box>
                )}

                {index < sections.length - 1 && (
                  <Divider sx={{ mt: 4, borderColor: "divider" }} />
                )}
              </Box>
            ))}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default PrivacyPolicyPage;
